import { useMemo } from 'react'
import {
  competenceLabel,
  evaluateResult,
  formatDuration,
  formatNumber,
  formatPercent,
  type MetricEvaluation,
  type PerformanceMonthlyResult,
  type PerformanceStatus,
} from '../lib/performance'

const STATUS_LABEL: Record<PerformanceStatus, string> = {
  exceeded: 'Meta superada',
  met: 'Meta atingida',
  near: 'Próximo da meta',
  attention: 'Atenção',
  missing: 'Sem dados',
}

function metricValue(metric: MetricEvaluation) {
  if (metric.metric === 'tma') return formatDuration(metric.value)
  if (metric.metric === 'nps' || metric.metric === 'participation') return formatPercent(metric.value)
  return formatNumber(metric.value)
}

function previousValue(history: PerformanceMonthlyResult[], current: PerformanceMonthlyResult, key: 'daily_average' | 'tma_seconds' | 'nps_score' | 'nps_participation_pct') {
  const sorted = [...history].sort((a, b) => b.competence.localeCompare(a.competence))
  const index = sorted.findIndex((item) => item.id === current.id)
  if (index < 0 || index + 1 >= sorted.length) return null
  return sorted[index + 1][key]
}

function deltaText(metric: MetricEvaluation, current: PerformanceMonthlyResult, history: PerformanceMonthlyResult[]) {
  if (metric.metric === 'daily') {
    const previous = previousValue(history, current, 'daily_average')
    if (previous == null || current.daily_average == null) return 'Sem mês anterior comparável'
    const delta = current.daily_average - previous
    return `${delta >= 0 ? '+' : ''}${formatNumber(delta)} vs mês anterior`
  }
  if (metric.metric === 'tma') {
    const previous = previousValue(history, current, 'tma_seconds')
    if (previous == null || current.tma_seconds == null) return 'Sem mês anterior comparável'
    const delta = current.tma_seconds - previous
    const minutes = Math.round(Math.abs(delta) / 60)
    return `${delta <= 0 ? '-' : '+'}${minutes} min vs mês anterior`
  }
  if (metric.metric === 'nps') {
    const previous = previousValue(history, current, 'nps_score')
    if (previous == null || current.nps_score == null) return 'Sem mês anterior comparável'
    const delta = current.nps_score - previous
    return `${delta >= 0 ? '+' : ''}${formatNumber(delta, 1)} p.p. vs mês anterior`
  }
  const previous = previousValue(history, current, 'nps_participation_pct')
  if (previous == null || current.nps_participation_pct == null) return 'Sem mês anterior comparável'
  const delta = current.nps_participation_pct - previous
  return `${delta >= 0 ? '+' : ''}${formatNumber(delta, 1)} p.p. vs mês anterior`
}

function statusClass(status: PerformanceStatus) {
  if (status === 'met' || status === 'exceeded') return 'value-good'
  if (status === 'near') return 'value-near'
  if (status === 'attention') return 'value-bad'
  return 'value-neutral'
}

function ProductivityChart({ history }: { history: PerformanceMonthlyResult[] }) {
  const ordered = [...history].sort((a, b) => a.competence.localeCompare(b.competence))
  const maxAttendances = Math.max(1, ...ordered.map((item) => item.total_attendances))
  const maxDaily = Math.max(1, ...ordered.map((item) => item.daily_average ?? 0))
  return <article className="performance-v52-chart-card">
    <div className="performance-v52-chart-title"><div><h3>Produtividade · Atendimentos e Média/dia</h3><span>Barras comparativas lado a lado</span></div><small>Últimos 3 meses</small></div>
    <div className="performance-v52-bar-chart">
      {ordered.map((item) => <div className="performance-v52-month-bars" key={`prod-${item.id}`}>
        <div className="performance-v52-bar-pair">
          <div className="performance-v52-bar performance-v52-bar-primary" style={{ height: `${Math.max(12, item.total_attendances / maxAttendances * 120)}px` }}><b>{formatNumber(item.total_attendances, 0)}</b></div>
          <div className="performance-v52-bar performance-v52-bar-secondary" style={{ height: `${Math.max(8, (item.daily_average ?? 0) / maxDaily * 52)}px` }}><b>{formatNumber(item.daily_average, 1)}</b></div>
        </div>
        <span>{competenceLabel(item.competence).split('/')[0].split(' de ')[0]}</span>
      </div>)}
    </div>
    <div className="performance-v52-legend"><span><i className="primary" />Atendimentos</span><span><i className="secondary" />Média/dia</span></div>
  </article>
}

function TmaChart({ history, current }: { history: PerformanceMonthlyResult[]; current: PerformanceMonthlyResult }) {
  const ordered = [...history].sort((a, b) => a.competence.localeCompare(b.competence))
  const values = ordered.map((item) => item.tma_seconds).filter((value): value is number => value != null)
  const target = current.target_tma_seconds
  const minValue = Math.min(target, ...(values.length ? values : [target]))
  const maxValue = Math.max(target, ...(values.length ? values : [target]))
  const range = Math.max(60, maxValue - minValue)
  const point = (value: number, index: number) => {
    const x = ordered.length <= 1 ? 300 : 42 + index * (516 / (ordered.length - 1))
    const y = 148 - ((value - minValue) / range) * 96
    return { x, y }
  }
  const points = ordered.map((item, index) => item.tma_seconds == null ? null : point(item.tma_seconds, index))
  const targetY = 148 - ((target - minValue) / range) * 96
  const polyline = points.filter((item): item is { x: number; y: number } => Boolean(item)).map((item) => `${item.x},${item.y}`).join(' ')
  return <article className="performance-v52-chart-card">
    <div className="performance-v52-chart-title"><div><h3>TMA</h3><span>Linha de tendência</span></div><small>Últimos 3 meses</small></div>
    <svg className="performance-v52-line-chart" viewBox="0 0 600 190" role="img" aria-label="Evolução do TMA">
      {[52, 84, 116, 148].map((y) => <line key={y} x1="42" y1={y} x2="558" y2={y} className="grid" />)}
      <line x1="42" y1={targetY} x2="558" y2={targetY} className="target" />
      {polyline && <polyline points={polyline} className="series" />}
      {points.map((item, index) => item && <g key={`tma-${ordered[index].id}`}><circle cx={item.x} cy={item.y} r="5" className="point" /><text x={item.x} y={item.y - 10} textAnchor="middle">{Math.round((ordered[index].tma_seconds ?? 0) / 60)}m</text><text x={item.x} y="174" textAnchor="middle" className="month">{competenceLabel(ordered[index].competence).split('/')[0].split(' de ')[0]}</text></g>)}
    </svg>
    <div className="performance-v52-legend"><span><i className="primary" />TMA</span><span><i className="secondary" />Meta de referência</span></div>
  </article>
}

function NpsChart({ history }: { history: PerformanceMonthlyResult[] }) {
  const ordered = [...history].sort((a, b) => a.competence.localeCompare(b.competence))
  return <article className="performance-v52-chart-card">
    <div className="performance-v52-chart-title"><div><h3>NPS e Participação NPS</h3><span>Barras comparativas lado a lado</span></div><small>Últimos 3 meses</small></div>
    <div className="performance-v52-bar-chart">
      {ordered.map((item) => <div className="performance-v52-month-bars" key={`nps-${item.id}`}>
        <div className="performance-v52-bar-pair">
          <div className="performance-v52-bar performance-v52-bar-primary" style={{ height: `${Math.max(8, (item.nps_score ?? 0) / 100 * 120)}px` }}><b>{formatNumber(item.nps_score, 1)}</b></div>
          <div className="performance-v52-bar performance-v52-bar-secondary" style={{ height: `${Math.max(6, (item.nps_participation_pct ?? 0) / 100 * 120)}px` }}><b>{formatNumber(item.nps_participation_pct, 1)}</b></div>
        </div>
        <span>{competenceLabel(item.competence).split('/')[0].split(' de ')[0]}</span>
      </div>)}
    </div>
    <div className="performance-v52-legend"><span><i className="primary" />NPS</span><span><i className="secondary" />Participação NPS</span></div>
  </article>
}

export function PerformanceV52View({ current, history, admin = false }: { current: PerformanceMonthlyResult; history: PerformanceMonthlyResult[]; admin?: boolean }) {
  const evaluations = useMemo(() => evaluateResult(current), [current])
  return <div className="performance-v52-view">
    <section className="performance-v52-hero">
      <div>
        <p className="eyebrow">{admin ? 'ACOMPANHAMENTO INDIVIDUAL' : 'RESULTADO DO MÊS'}</p>
        <h2>{current.employee_name}</h2>
        <p>{current.units.join(' · ')} · {competenceLabel(current.competence)}</p>
        <small>{admin ? 'Leitura consolidada do colaborador para acompanhamento gerencial e orientação de desenvolvimento.' : 'Acompanhe seus resultados mensais e os principais indicadores desta competência.'}</small>
      </div>
    </section>

    <div className="performance-v52-kpis">
      <article className="performance-v52-kpi neutral"><span>Atendimentos</span><strong>{formatNumber(current.total_attendances, 0)}</strong><small>Volume total no mês</small><em>Indicador informativo</em></article>
      {evaluations.map((metric) => <article className={`performance-v52-kpi ${statusClass(metric.status)}`} key={metric.metric}>
        <span>{metric.label === 'Média diária' ? 'Média/dia' : metric.label}</span>
        <strong>{metricValue(metric)}</strong>
        <small>Meta {metric.metric === 'tma' ? `≤ ${formatDuration(metric.target)}` : metric.metric === 'daily' ? formatNumber(metric.target) : formatPercent(metric.target)}</small>
        <div className="performance-v52-kpi-foot"><em>{deltaText(metric, current, history)}</em><b className={`performance-v52-status ${metric.status}`}>{STATUS_LABEL[metric.status]}</b></div>
      </article>)}
    </div>

    <div className="performance-v52-chart-row">
      <ProductivityChart history={history} />
      <TmaChart history={history} current={current} />
      <NpsChart history={history} />
    </div>
  </div>
}
